/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { modelType } from '@24i/st-api-connector';
import { Device } from 'DeviceUtils';
// eslint-disable-next-line import/no-unresolved
import { playerModalsEnum } from 'LocalGluons/components/PlayerUI/components/PlayerModal';
import React, { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'Store';
import type { PlayerData, StreamData } from 'Types';
// eslint-disable-next-line import/no-unresolved
import { BINGE_WATCHING_LOADING_DURATION, STREAM_TYPE } from 'Options';
import { isArchive } from 'Utils';
import { PlayerManager } from '@24i/react-native-player';
import { SeekType } from '@24i/player-base';
import { useChromecastContext } from 'LocalGluons/components/ChromecastContext';
import {
    canUseBingeWatching,
    isSeriesTypeLike,
    shouldHaveContinueWatching
} from '../playerContainerUtils';
import useAdvertisements from './useAdvertisements';
// import { shouldSwitchToTimeshift } from '../playerUtils';
import useBingeWatching from './useBingeWatching';
import useContinueWatching from './useContinueWatching';
import useLivePlayer from './useLivePlayer';

const usePlayerData = (
    setCurrentTime: (time: number) => void,
    currentAssetTime: number | null,
    playerData: PlayerData,
    // changeItem: (item: PlayerData['item']) => void,
    itemStreamData: StreamData,
    player: PlayerManager,
    playerInstance,
    onPlayerBack?: (() => void) | undefined,
    onPlayerProgress?: (progressDataObject: any) => void,
) => {
    const { setMediaState } = useChromecastContext();
    const { setPlayerData } = useStore();
    const { item, offline = false, isTrailer = false } = playerData;
    const { streamType } = itemStreamData;
    const [modalType, setModalType] = useState<playerModalsEnum | null>(null);
    const isModalBinge = modalType === playerModalsEnum.bingeWatching;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isLiveVOD = streamType === STREAM_TYPE.LIVE && item.getChannelType() === STREAM_TYPE.VOD;

    const changeItem = useCallback((newItem: PlayerData['item'], shouldRewindToStart = false, isLiveRecording = false) => {
        setMediaState && setMediaState((state) => ({
            ...state,
            isPaused: false
        }));
        setCurrentTime(0);
        setPlayerData({ ...playerData, shouldRewindToStart, item: newItem, isLiveRecording });
    }, [setCurrentTime, setPlayerData]);

    // (isSeriesTypeLike(item) || canUseBingeWatching(item) || isArchive(item))

    // (item.modelType === modelType.tv)
    const {
        isVideoBlocked,
        onHorizontalGesture,
        liveItem,
        refreshDetailItem,
        onProgress: onLiveProgress,
        onBack,
        loading,
        onResumePlaying: onLiveResume,
        playNextItem,
        playPreviousItem,
        archiveAvailable,
        switchToLive,
        liveLoading,
        setLiveLoading,
        setNewCurrentItem
    } = useLivePlayer(item, changeItem, itemStreamData, onPlayerBack,
        isModalBinge, setModalType, player, onPlayerProgress);

    const {
        onModalHidden,
        onProgress: bingeWatchingOnProgress,
        nextBingeItem,
        updateBingeItem
    } = useBingeWatching(
        item,
        changeItem,
        onPlayerProgress,
        modalType,
        setModalType,
        currentAssetTime,
        liveLoading,
        isLiveVOD,
        playerInstance,
        player
    );

    const {
        adBlocks,
        fetchAds,
        gracePeriodInSeconds,
        setGracePeriodInSeconds,
        disableForwardSeeking,
        adSkipPolicy,
        mediaHouse
    } = useAdvertisements(
        item,
        itemStreamData
    );

    // (shouldHaveContinueWatching(item, offline, isTrailer)) {
    const {
        onProgress: continueWatchingOnProgress,
        onPause,
        onVideoSeek: continueWatchingVideoSeek
    } = useContinueWatching(item, onPlayerProgress);

    const onProgress = useCallback(
        (event) => {
            if (isSeriesTypeLike(item) || canUseBingeWatching(item) || isArchive(item) || isLiveVOD)
                bingeWatchingOnProgress(event);

            if (item.modelType === modelType.tv) onLiveProgress(event);

            if (shouldHaveContinueWatching(item, offline, isTrailer))
                continueWatchingOnProgress(event);
        },
        [
            item,
            bingeWatchingOnProgress,
            onLiveProgress,
            offline,
            isTrailer,
            continueWatchingOnProgress
        ]
    );

    const onResumePlaying = useCallback(
        (currentTime: number) => {
            if (item.modelType === modelType.tv) onLiveResume(currentTime);
        },
        [item.modelType, onLiveResume]
    );

    const onVideoSeek = useCallback(
        (currentTime: number, duration: number, type: string) => {
            if (shouldHaveContinueWatching(item, offline, isTrailer)) continueWatchingVideoSeek();
            // player doesn't update time during seeking so binge doesn't update too
            // if (currentTime === duration) {
            //     setLiveLoading(true);
            // }
            const updatedCurrentTime = currentTime > playerInstance.currentTime
                ? playerInstance.currentTime
                : currentTime;
            if (!isFinite(duration) || !duration) return;
            if ((updatedCurrentTime >= duration - BINGE_WATCHING_LOADING_DURATION) &&
                modalType !== playerModalsEnum.bingeWatching && nextBingeItem && type !== SeekType.UTC) {
                // (itemStreamData.streamType === STREAM_TYPE.ARCHIVE ||
                //     itemStreamData.streamType === STREAM_TYPE.VOD)) {
                setModalType(playerModalsEnum.bingeWatching);
            }
        },
        [item, offline, isTrailer, continueWatchingVideoSeek]
    );

    const programStart = useMemo(() => ('getStartTime' in item ? item.getStartTime() : 0), [item]);
    const programEnd = useMemo(() => ('getEndTime' in item ? item.getEndTime() : 0), [item]);

    return {
        programStart,
        programEnd,
        modalType,
        setModalType,
        showModal: !!modalType,
        onPause,
        onModalHidden,
        onProgress,
        isVideoBlocked,
        onHorizontalGesture,
        onVideoSeek,
        liveItem,
        refreshDetailItem,
        onBack,
        loading,
        playNextItem,
        playPreviousItem,
        archiveAvailable,
        nextBingeItem,
        adBlocks,
        fetchAds,
        gracePeriodInSeconds,
        setGracePeriodInSeconds,
        disableForwardSeeking,
        switchToLive,
        setLiveLoading,
        liveLoading,
        isModalBinge,
        isLiveVOD,
        changeItem,
        setNewCurrentItem,
        adSkipPolicy,
        mediaHouse,
        updateBingeItem
    };
};

export default usePlayerData;
